import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import localforage from "localforage";
import logo from "../assets/akar-logo-white.png";
import api from "../utils/axiosConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHouse,
  faCalendarAlt,
  faUsersLine,
  faSignOutAlt,
  faFolder,
  faDiagramProject,
  faComments,
  faIdBadge,
  faBuilding,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";

const API_URL = process.env.REACT_APP_API_URL;

/* ─────────────────────────────────────────────────────────
   Avatar Dropdown
───────────────────────────────────────────────────────── */
const AvatarDropdown = ({ userName, email, onLogout }) => {
  const [open, setOpen] = useState(false);
  const [employeeCode, setEmployeeCode] = useState("");
  const [department, setDepartment] = useState("");
  const [loadingProfile, setLoadingProfile] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoadingProfile(true);
      try {
        const token = await localforage.getItem("token");
        if (!token) return;
        const res = await api.get(`${API_URL}/leavedata/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEmployeeCode(res.data?.employeeCode || "");
        setDepartment(res.data?.department || "");
      } catch {
        /* ignore */
      } finally {
        setLoadingProfile(false);
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="avatar-dropdown-wrap" ref={dropRef}>
      <button
        className={`app-header-avatar-btn ${open ? "open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-label="Account menu"
      >
        <span className="app-header-avatar">{getInitials(userName)}</span>
        <FontAwesomeIcon icon={faChevronDown} className="avatar-chevron" />
      </button>

      {open && (
        <div className="avatar-dropdown">
          <div className="avatar-drop-banner">
            <div className="avatar-drop-circle">{getInitials(userName)}</div>
            <div className="avatar-drop-info">
              <span className="avatar-drop-name">{userName || "User"}</span>
              <span className="avatar-drop-email">{email || ""}</span>
            </div>
          </div>

          <div className="avatar-drop-details">
            {loadingProfile ? (
              <div className="avatar-drop-loading">Loading profile…</div>
            ) : (
              <>
                <div className="avatar-drop-row">
                  <div className="avatar-drop-row-icon">
                    <FontAwesomeIcon icon={faIdBadge} />
                  </div>
                  <div>
                    <span className="avatar-drop-row-label">Employee Code</span>
                    <span className="avatar-drop-row-value">
                      {employeeCode || "—"}
                    </span>
                  </div>
                </div>
                <div className="avatar-drop-row">
                  <div className="avatar-drop-row-icon">
                    <FontAwesomeIcon icon={faBuilding} />
                  </div>
                  <div>
                    <span className="avatar-drop-row-label">Department</span>
                    <span className="avatar-drop-row-value">
                      {department || "—"}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="avatar-drop-footer">
            <button className="avatar-drop-logout" onClick={onLogout}>
              <FontAwesomeIcon icon={faSignOutAlt} />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   Header Quick-Nav Icon button
───────────────────────────────────────────────────────── */
const HeaderNavBtn = ({ icon, label, badge, active, onClick }) => (
  <button
    className={`header-nav-btn ${active ? "header-nav-btn--active" : ""}`}
    onClick={onClick}
    title={label}
    aria-label={label}
  >
    <FontAwesomeIcon icon={icon} className="header-nav-btn__icon" />
    {badge > 0 && (
      <span className="header-nav-btn__badge">
        {badge > 99 ? "99+" : badge}
      </span>
    )}
  </button>
);

/* ─────────────────────────────────────────────────────────
   SideBar
───────────────────────────────────────────────────────── */
const SideBar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  /* ── Bootstrap user data ────────────────────────────── */
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const [storedName, storedEmail, storedRole] = await Promise.all([
          localforage.getItem("userName"),
          localforage.getItem("email"),
          localforage.getItem("role1"),
        ]);
        if (storedName) setUserName(storedName);
        if (storedEmail) setEmail(storedEmail);
        if (storedRole) setRole(storedRole);
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };
    fetchUserData();
  }, []);

  /* ── Fetch unread chat count ────────────────────────── */
  const fetchUnread = useCallback(async () => {
    try {
      const [token, userEmail] = await Promise.all([
        localforage.getItem("token"),
        localforage.getItem("email"),
      ]);
      if (!token || !userEmail) return;

      const res = await api.get(`${API_URL}/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const conversations =
        res.data?.data || res.data?.conversations || res.data || [];

      // Each conversation may carry an unreadCount field from the backend,
      // or we derive it from the latest message's readBy array.
      let total = 0;
      conversations.forEach((conv) => {
        if (typeof conv.unreadCount === "number") {
          total += conv.unreadCount;
        } else if (conv.lastMessage && Array.isArray(conv.lastMessage.readBy)) {
          // Count as unread if current user is not in readBy and is not the sender
          const notRead = !conv.lastMessage.readBy.includes(
            userEmail.toLowerCase(),
          );
          const notSender = conv.lastMessage.sender !== userEmail.toLowerCase();
          if (notRead && notSender) total += 1;
        }
      });

      setUnreadCount(total);
    } catch {
      /* silently ignore — badge just stays at 0 */
    }
  }, []);

  /* Poll every 30 s; also fetch immediately on mount */
  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30_000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  /* Clear badge when user lands on /chats */
  useEffect(() => {
    if (location.pathname === "/chats") setUnreadCount(0);
  }, [location.pathname]);

  const toggleSidebar = () => setSidebarOpen((v) => !v);
  const closeSidebar = () => setSidebarOpen(false);
  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    try {
      await localforage.clear();
      navigate("/");
    } catch (err) {
      console.error("Error during logout:", err);
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const MenuItem = ({ to, icon, text }) => (
    <div
      className={`sidebar-menu-item ${isActive(to) ? "active" : ""}`}
      onClick={() => {
        navigate(to);
        closeSidebar();
      }}
    >
      <FontAwesomeIcon icon={icon} className="sidebar-menu-icon" />
      <span className="sidebar-menu-text">{text}</span>
    </div>
  );

  const getMenuItems = () => {
    const items = [
      { to: "/welcome", icon: faHouse, text: "Home" },
      { to: "/workspace", icon: faDiagramProject, text: "My Week" },
      { to: "/chats", icon: faComments, text: "Chat" },
    ];
    if (role !== "ops") {
      items.push({
        to: "/leave-section",
        icon: faCalendarAlt,
        text: "Leave Section",
      });
    }
    if (role === "admin") {
      items.push({
        to: "/userdashboard",
        icon: faUsersLine,
        text: "User Management",
      });
      items.push({
        to: "/influencer-leaderboard",
        icon: faFolder,
        text: "Influencer Dashboard",
      });
    }
    return items;
  };

  return (
    <>
      {/* ── Persistent top header ──────────────────────────────── */}
      <header className="app-header">
        {/* Left: grid menu + logo */}
        <div className="app-header-left">
          <button
            className={`app-header-grid-btn ${sidebarOpen ? "active" : ""}`}
            onClick={toggleSidebar}
            aria-label="Toggle menu"
          >
            <span className="grid-dot-icon">
              {[...Array(9)].map((_, i) => (
                <span key={i} className="grid-dot" />
              ))}
            </span>
          </button>

          <img
            src={logo}
            alt="AKAR Logo"
            className="app-header-logo-img"
            onClick={() => navigate("/welcome")}
          />
        </div>

        {/* Centre: quick-nav icons */}
        <div className="app-header-quicknav"></div>

        {/* Right: avatar dropdown */}
        <div className="app-header-right">
          <HeaderNavBtn
            icon={faDiagramProject}
            label="My Week"
            active={isActive("/workspace")}
            onClick={() => navigate("/workspace")}
          />
          <HeaderNavBtn
            icon={faComments}
            label="Chat"
            badge={unreadCount}
            active={isActive("/chats")}
            onClick={() => navigate("/chats")}
          />
          <AvatarDropdown
            userName={userName}
            email={email}
            onLogout={handleLogout}
          />
        </div>
      </header>

      {/* ── Sidebar overlay ────────────────────────────────────── */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? "active" : ""}`}
        onClick={closeSidebar}
      />

      {/* ── Sidebar drawer ─────────────────────────────────────── */}
      <div className={`sidebar ${sidebarOpen ? "active" : ""}`}>
        <div className="sidebar-menu">
          {getMenuItems().map((item, index) => (
            <MenuItem
              key={index}
              to={item.to}
              icon={item.icon}
              text={item.text}
            />
          ))}
        </div>

        <div className="sidebar-footer">
          <div className="sidebar-user-info">
            <div className="sidebar-user-avatar">{getInitials(userName)}</div>
            <div className="sidebar-user-details">
              <div className="sidebar-user-name">{userName || "User"}</div>
              <div className="sidebar-user-email">
                {email || "user@example.com"}
              </div>
            </div>
          </div>
          <button className="sidebar-logout-btn" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* ── Scoped styles ──────────────────────────────────────── */}
      <style>{`

        /* ── Quick-nav pill container ───────────────────────────── */
        .app-header-quicknav {
          display: flex;
          align-items: center;
          gap: 6px;
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
        }

        /* ── Individual icon button ─────────────────────────────── */
        .header-nav-btn {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          margin: 5px;
          border-radius: 10px;
          border: none;
          background: rgba(255,255,255,0.10);
          color: rgba(255,255,255,0.75);
          cursor: pointer;
          transition: background 0.18s, color 0.18s, transform 0.15s;
          outline: none;
        }

        .header-nav-btn:hover {
          background: rgba(255,255,255,0.22);
          color: #fff;
          transform: translateY(-1px);
        }

        .header-nav-btn--active {
          background: rgba(255,255,255,0.22);
          color: #fff;
          box-shadow: inset 0 0 0 1.5px rgba(255,255,255,0.35);
        }

        .header-nav-btn__icon {
          font-size: 15px;
        }

        /* ── Unread badge ───────────────────────────────────────── */
        .header-nav-btn__badge {
          position: absolute;
          top: -5px;
          right: -5px;
          min-width: 18px;
          height: 18px;
          padding: 0 4px;
          border-radius: 20px;
          background: #fff;
          color: #CE452B;
          font-size: 0.62rem;
          font-weight: 800;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid #CE452B;
          line-height: 1;
          box-shadow: 0 1px 4px rgba(0,0,0,0.18);
          animation: badgePop 0.25s cubic-bezier(0.34,1.56,0.64,1) both;
        }

        @keyframes badgePop {
          from { transform: scale(0.4); opacity: 0; }
          to   { transform: scale(1);   opacity: 1; }
        }

        /* ── Avatar trigger ─────────────────────────────────────── */
        .avatar-dropdown-wrap { position: relative;
        margin: 5px;}

        .app-header-avatar-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          background: rgba(255,255,255,0.15);
          border: 1.5px solid rgba(255,255,255,0.25);
          border-radius: 40px;
          padding: 4px 10px 4px 4px;
          cursor: pointer;
          transition: background 0.18s, border-color 0.18s;
          outline: none;
        }
        .app-header-avatar-btn:hover,
        .app-header-avatar-btn.open {
          background: rgba(255,255,255,0.25);
          border-color: rgba(255,255,255,0.5);
        }

        .app-header-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #fff;
          color: #CE452B;
          font-size: 0.78rem;
          font-weight: 800;
          display: flex;
          align-items: center;
          justify-content: center;
          letter-spacing: 0.02em;
          flex-shrink: 0;
        }

        .avatar-chevron {
          color: rgba(255,255,255,0.8);
          font-size: 10px;
          transition: transform 0.22s ease;
        }
        .app-header-avatar-btn.open .avatar-chevron {
          transform: rotate(180deg);
        }

        /* ── Dropdown panel ─────────────────────────────────────── */
        .avatar-dropdown {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          width: 260px;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 8px 40px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.06);
          overflow: hidden;
          z-index: 9999;
          animation: dropIn 0.18s cubic-bezier(0.34,1.3,0.64,1) both;
        }

        @keyframes dropIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }

        .avatar-drop-banner {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 18px 18px 16px;
          background: linear-gradient(135deg, #CE452B 0%, #b33a22 100%);
        }

        .avatar-drop-circle {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: rgba(255,255,255,0.22);
          border: 2px solid rgba(255,255,255,0.4);
          color: #fff;
          font-size: 1rem;
          font-weight: 800;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          letter-spacing: 0.02em;
        }

        .avatar-drop-info { display: flex; flex-direction: column; min-width: 0; }

        .avatar-drop-name {
          font-size: 0.92rem;
          font-weight: 700;
          color: #fff;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .avatar-drop-email {
          font-size: 0.72rem;
          color: rgba(255,255,255,0.75);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin-top: 2px;
        }

        .avatar-drop-details {
          padding: 12px 16px 8px;
          border-bottom: 1px solid #f3f3f3;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .avatar-drop-loading {
          font-size: 0.78rem;
          color: #aaa;
          padding: 6px 0;
          text-align: center;
        }

        .avatar-drop-row {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .avatar-drop-row-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: #f5f5f5;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #CE452B;
          font-size: 13px;
          flex-shrink: 0;
        }

        .avatar-drop-row-label {
          display: block;
          font-size: 0.66rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #aaa;
        }

        .avatar-drop-row-value {
          display: block;
          font-size: 0.85rem;
          font-weight: 700;
          color: #1A2A4A;
          margin-top: 1px;
        }

        .avatar-drop-footer { padding: 10px 14px 12px; }

        .avatar-drop-logout {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: #fff5f4;
          border: 1.5px solid #ffd5cf;
          border-radius: 10px;
          padding: 9px 16px;
          color: #CE452B;
          font-size: 0.82rem;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.15s, border-color 0.15s;
        }
        .avatar-drop-logout:hover {
          background: #ffebe8;
          border-color: #CE452B;
        }

        /* ── Responsive: hide centre icons on very small screens ── */
        @media (max-width: 480px) {
          .app-header-quicknav { display: none; }
        }
      `}</style>
    </>
  );
};

export default SideBar;
